import { Examination, Examination_Type, Grade, Ml_Log_Status, Trial } from "@generated/graphql";
import { ExaminationItem } from "components/ExaminationList/types";
import { ISelectItem } from "components/inputs/Select";
import { WORD_DECLENSIONS } from "constants/declensions";
import ROUTES from "constants/routes";
import dayjs from "dayjs";
import { Maybe } from "graphql/jsutils/Maybe";

const getWordDeclension = (count: number, declensions: keyof typeof WORD_DECLENSIONS | string[]) => {
  const [one, few, many] = Array.isArray(declensions) ? declensions : WORD_DECLENSIONS[declensions];
  const absoluteCount = Math.abs(count);
  const lastTwoDigits = absoluteCount % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return many;
  }

  const lastDigit = absoluteCount % 10;

  if (lastDigit === 1) {
    return one;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return few;
  }

  return many;
};

const formatNumber = (titles: string[], num: number | null | undefined, onlyTitle?: boolean) => {
  const currentNumber = num ?? 0;
  // eslint-disable-next-line max-len
  return `${onlyTitle ? "" : `${Intl.NumberFormat().format(currentNumber)} `}${getWordDeclension(currentNumber, titles)}`;
};

const generateOptions = (object: object) =>
  Object.entries(object).reduce((acc: object[], current: string[]) => {
    const newValue = {
      displayName: current[1],
      id: current[0],
    };
    acc.push(newValue);
    return acc;
  }, []);

const generateTitleMapFromSelectItems = <TSelectItem extends ISelectItem>(items: TSelectItem[]) => {
  return items.reduce<Record<TSelectItem["value"], TSelectItem["label"]>>(
    (acc, item) => ({
      ...acc,
      [item.value]: item.label,
    }),
    {} as Record<TSelectItem["value"], TSelectItem["label"]>
  );
};

const gradeColor = (grade: Grade) => {
  if (grade === Grade.Junior) return "green";
  if (grade === Grade.Middle) return "orange";
  return "red";
};

const confirmationColor = (confirmation: boolean) => {
  if (confirmation) return "green";
  return "orange";
};

const createUserName = (
  user:
    | {
        firstName?: string | null | undefined;
        lastName?: string | null | undefined;
        middleName?: string | null | undefined;
      }
    | undefined
    | null
) => {
  if (!user) return "—";
  const { firstName, lastName, middleName } = user;
  return firstName || lastName || middleName ? [firstName, lastName, middleName].join(" ") : "—";
};

const getInitials = (name: string = "", join: string = "") => {
  const nameArr = name
    .replace(/\s+/, " ")
    .split(" ")
    .slice(0, 2)
    .map((v) => v && v[0].toUpperCase());
  return nameArr.length === 0 || nameArr.length === 1 ? nameArr.join("") : nameArr.join(join);
};

const getTestingPath = (type: "trial" | "examination", id?: string) => {
  let path = `/${ROUTES.TESTING}/${id ? "view" : "create"}`;

  if (type === "trial") {
    path = `${path}/${ROUTES.PERSONAL_TESTING}`;
  } else {
    path = `${path}/${ROUTES.GROUP_TESTING}`;
  }

  if (id) {
    path = `${path}/${id}`;
  }

  return path;
};

const secondsToMinutes = (seconds: number | null | undefined, defaultText?: string) =>
  seconds ? dayjs.duration(seconds, "s").format("HH:mm:ss") : defaultText || "-";

const formatPhoneNumber = (phoneNumber: string | null | undefined): string | null => {
  if (!phoneNumber) return null;
  const digits = phoneNumber.replace(/\D/g, "");
  const core = digits.replace(/^8/, "7").replace(/^7/, "");

  if (core.length !== 10) return null;

  return `+7 (${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6, 8)}-${core.slice(8)}`;
};

const toggleItemInArray = <T extends string | number | unknown = string>(selected: T[], id: T) => {
  const stringSelected = selected.map((item) => String(item));
  const stringId = String(id);
  const selectedIndex = stringSelected.indexOf(stringId);
  const newSelected: string[] = [];

  if (selectedIndex === -1) {
    return newSelected.concat(stringSelected, stringId);
  }
  if (selectedIndex === 0) {
    return newSelected.concat(stringSelected.slice(1));
  }
  if (selectedIndex === selected.length - 1) {
    return newSelected.concat(stringSelected.slice(0, -1));
  }

  return newSelected.concat(stringSelected.slice(0, selectedIndex), stringSelected.slice(selectedIndex + 1));
};

const getPersonalExaminationLink = (trialId: Trial["id"], telegramUsername: Maybe<string>) => {
  return telegramUsername
    ? `https://t.me/${process.env.REACT_APP_BOT_URL}?start=${trialId}`
    : `${process.env.REACT_APP_ASSESSMENT_URL}/login`;
};

const getOpenExaminationLink = (examinationId: Examination["id"]) => {
  return `${process.env.REACT_APP_ASSESSMENT_URL}/examination/${examinationId}`;
};

const getPublicExaminationLink = (examinationId: Examination["id"]) => {
  return `${process.env.REACT_APP_ASSESSMENT_URL}/public-examination/${examinationId}`;
};

const getTestitngTypeTitle = (questionsTime: number | null | undefined) => {
  if (!questionsTime) return undefined;
  if (questionsTime <= 1800) return "Скриннинг";
  if (questionsTime > 1800 && questionsTime <= 7200) return "Ассесмент";
  return "Расширенный ассесмент";
};

const getQuestionsGenLogsStatus = (logs: { status: Ml_Log_Status }[] | undefined) => {
  if (!logs || !logs.length) return null;

  let status = Ml_Log_Status.Success;

  for (const log of logs) {
    if (log.status === Ml_Log_Status.Initialized) {
      status = Ml_Log_Status.Initialized;
      break;
    }

    if (log.status !== Ml_Log_Status.Success) {
      status = Ml_Log_Status.Error;
    }
  }

  return status;
};

export const getTrialLinkString = (
  examination: {
    __typename?: "Examination";
    id: string;
    type: Examination_Type;
    questionsTime?: number | null;
  },
  trial?: NonNullable<ExaminationItem["trials"]>[number]
): string | undefined => {
  switch (examination.type) {
    case Examination_Type.Personal:
    case Examination_Type.Group: {
      if (!trial) return undefined;

      return getPersonalExaminationLink(trial.id, trial.companyCandidate.candidateUsername?.telegramUsername);
    }
    case Examination_Type.Open:
      return getOpenExaminationLink(examination.id);
    case Examination_Type.Public:
      return getPublicExaminationLink(examination.id);
    default:
      return undefined;
  }
};

const getIdsSearchParam = (id: string, searchParamIds: string | undefined, acceptSlice: boolean = true) => {
  const searchParamIdsArr = searchParamIds?.split(",") || [];
  const searchParamIdsEntityIndex = searchParamIdsArr?.findIndex((paramId) => paramId === id);

  if (searchParamIdsEntityIndex === -1) {
    return [...searchParamIdsArr, id];
  }

  if (acceptSlice) {
    return [
      ...searchParamIdsArr.slice(0, searchParamIdsEntityIndex),
      ...searchParamIdsArr.slice(searchParamIdsEntityIndex + 1),
    ];
  }

  return searchParamIdsArr;
};

const getSearchParamsWithIdsParam = (
  id: string,
  paramName: string,
  params: Record<string, string>,
  acceptSlice: boolean = true
) => {
  const searchParamIds = params[paramName] || undefined;
  const entityIdsParamValue = getIdsSearchParam(id, searchParamIds, acceptSlice).join(",");
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { [paramName]: _, ...otherParams } = params;

  if (entityIdsParamValue) {
    return { ...otherParams, [paramName]: entityIdsParamValue };
  }

  if (searchParamIds) {
    return otherParams;
  }

  return null;
};

export {
  confirmationColor,
  createUserName,
  formatNumber,
  formatPhoneNumber,
  generateOptions,
  generateTitleMapFromSelectItems,
  getIdsSearchParam,
  getInitials,
  getOpenExaminationLink,
  getPersonalExaminationLink,
  getPublicExaminationLink,
  getQuestionsGenLogsStatus,
  getSearchParamsWithIdsParam,
  getTestingPath,
  getTestitngTypeTitle,
  getWordDeclension,
  gradeColor,
  secondsToMinutes,
  toggleItemInArray,
};
